import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Check, Download as DownloadIcon, Mic, BrainCircuit, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import useVersions from '../lib/useVersions';
import { Button } from '../components/ui/button';
import { formatDate } from '../lib/utils';

const APP_META = {
  recorder: { icon: Mic, name: 'Call Recorder', fallbackUrl: '/downloads/CallRecorder-Installer.zip' },
  processor: { icon: BrainCircuit, name: 'Call Processor', fallbackUrl: '/downloads/CallProcessor-Installer.zip' },
};

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      /* clipboard unavailable */
    }
  };
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="mt-1.5 flex items-center gap-2">
        <code className="flex-1 truncate rounded-lg border border-border bg-muted/60 px-3 py-2 text-sm text-foreground">
          {value || '—'}
        </code>
        {value && (
          <button
            onClick={copy}
            aria-label={`Copy ${label}`}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground hover:text-foreground"
          >
            {copied ? <Check size={16} className="text-accent" /> : <Copy size={16} />}
          </button>
        )}
      </div>
    </div>
  );
}

function VersionRow({ appKey, manifest, loading }) {
  const meta = APP_META[appKey];
  const url = manifest?.url || meta.fallbackUrl;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-background/50 p-4">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-accent/15 dark:text-accent">
          <meta.icon size={18} />
        </span>
        <div>
          <p className="text-sm font-semibold text-foreground">{meta.name}</p>
          <p className="text-xs text-muted-foreground">
            {loading ? 'Checking latest…' : manifest?.version ? `Latest available: v${manifest.version}` : 'No release published yet'}
          </p>
        </div>
      </div>
      <a href={url} download>
        <Button variant="outline" size="sm">
          <DownloadIcon size={15} /> Download
        </Button>
      </a>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data, loading } = useVersions();

  return (
    <div className="container-page py-16">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Your dashboard
          </h1>
          <p className="mt-2 text-muted-foreground">
            Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}.
          </p>
        </div>
        <Link to="/download">
          <Button>
            <DownloadIcon size={17} /> Download apps
          </Button>
        </Link>
      </div>

      <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_1.15fr]">
        {/* Account */}
        <div className="rounded-2xl border border-border bg-card p-7 shadow-card">
          <h2 className="font-display text-lg font-semibold text-foreground">Account</h2>
          <div className="mt-5 space-y-5">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Name</p>
              <p className="mt-1 text-sm text-foreground">{user?.name || '—'}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Email</p>
              <p className="mt-1 text-sm text-foreground">{user?.email || '—'}</p>
            </div>
            {user?.created_at && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Member since</p>
                <p className="mt-1 text-sm text-foreground">{formatDate(user.created_at)}</p>
              </div>
            )}
            <CopyField label="Account token" value={user?.account_token} />
            <p className="text-xs text-muted-foreground">
              Your account token identifies your install for updates and support. Keep it private.
            </p>
          </div>
        </div>

        {/* Apps / versions */}
        <div className="rounded-2xl border border-border bg-card p-7 shadow-card">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-foreground">Your apps</h2>
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <RefreshCw size={13} /> auto-updating
            </span>
          </div>
          <div className="mt-5 space-y-3">
            <VersionRow appKey="recorder" manifest={data?.recorder} loading={loading} />
            <VersionRow appKey="processor" manifest={data?.processor} loading={loading} />
          </div>
          <div className="mt-5 rounded-xl bg-muted/50 p-4">
            <p className="text-sm text-foreground">Your installed version vs latest</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Both desktop apps compare their built-in version against the latest shown here on every
              launch and update themselves automatically. If an app is behind, just relaunch it — no
              manual reinstall needed.
            </p>
            <Link to="/setup" className="mt-3 inline-block text-sm font-medium text-primary hover:underline dark:text-accent">
              View the setup guide →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
